import axios, { type AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';
import { isErrorEnvelope, type ErrorEnvelope } from '@dts/shared';

/** Métodos HTTP que exigem CSRF token. */
const UNSAFE_METHODS = new Set(['post', 'put', 'patch', 'delete']);

/** Lê o cookie pelo nome (não-httpOnly). */
function readCookie(name: string): string | undefined {
  if (typeof document === 'undefined') return undefined;
  const target = `${name}=`;
  for (const raw of document.cookie.split(';')) {
    const c = raw.trim();
    if (c.startsWith(target)) return c.slice(target.length);
  }
  return undefined;
}

/**
 * Listener chamado quando a sessão é detectada como expirada (401).
 * `AuthContext` registra um listener para limpar estado local. Usa `Set`
 * para suportar StrictMode (monta/desmonta efeito 2× em dev) sem que a
 * segunda montagem zere o handler da primeira.
 */
type SessionExpiredListener = (codigo: string) => void;
const sessionExpiredListeners = new Set<SessionExpiredListener>();

/**
 * Registra um listener. Retorna a função de unregister.
 * Passar `null` limpa todos os listeners (útil em testes).
 */
export function onSessionExpired(listener: SessionExpiredListener | null): () => void {
  if (listener === null) {
    sessionExpiredListeners.clear();
    return () => undefined;
  }
  sessionExpiredListeners.add(listener);
  return () => {
    sessionExpiredListeners.delete(listener);
  };
}

export const api: AxiosInstance = axios.create({
  baseURL: '/api',
  withCredentials: true,
  // Sempre devolve a resposta para o handler — o erro de negócio vem em status real.
  validateStatus: (s) => s >= 200 && s < 300,
});

// Request: injeta CSRF token em métodos não-seguros.
api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const method = (config.method ?? 'get').toLowerCase();
  if (UNSAFE_METHODS.has(method)) {
    const csrf = readCookie('dts_csrf');
    if (csrf) {
      config.headers.set('X-CSRF-Token', csrf);
    }
  }
  return config;
});

// Response: 401 → notifica sessão expirada (sem retry — GAM não tem refresh).
api.interceptors.response.use(
  (r) => r,
  (error: AxiosError<ErrorEnvelope>) => {
    if (error.response?.status === 401) {
      const codigo = isErrorEnvelope(error.response.data)
        ? error.response.data.erro.codigo
        : 'NAO_AUTENTICADO';
      for (const listener of sessionExpiredListeners) {
        listener(codigo);
      }
    }
    return Promise.reject(error);
  },
);
